import React, { useCallback, useState } from "react";

import {
  useMutationAddFavorite,
  useMutationRemoveFavorite,
  useQueryIsUserFavorite,
  useAppContext,
} from "@examspark/common-app";
import { Button, Spinner } from "@examspark/common-ui";
import { IconStarSolid } from "@examspark/common-ui/dist/components/icons";
import { IDSK } from "@examspark/types";

type Props = {
  item?: IDSK;
  disabled?: boolean;
  onChange?: (value: boolean) => void;
};

const FavoriteButton = ({ item, disabled, onChange }: Props) => {
  const [updating, setUpdating] = useState(false);
  const [state] = useAppContext();
  const { data: isUserFavorite } = useQueryIsUserFavorite(item, state.user?.id);

  const removeMutation = useMutationRemoveFavorite();
  const addMutation = useMutationAddFavorite();

  const onChangeInternal = useCallback(
    (isUserFavorite: boolean) => {
      setUpdating(false);
      onChange?.(isUserFavorite);
    },
    [onChange],
  );

  const ready =
    item != null &&
    state.user?.id != null &&
    state.workspaceUser?.id != null &&
    isUserFavorite != null &&
    !updating;

  return (
    <Button
      style={{ color: isUserFavorite ? "#F4C84B" : "#aaa" }}
      variant="plain"
      disabled={disabled || !ready}
      title={isUserFavorite ? "Remove from favorites" : "Add to favorites"}
      onClick={(e) => {
        if (disabled || !ready) return;

        // if ready is true, we know these are non-null
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const userId = state.user!.id;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const workspaceId = state.workspaceUser!.id;

        setUpdating(true);

        if (isUserFavorite) {
          removeMutation.mutate(
            {
              id: item.id,
              sk: item.sk,
              userId,
            },
            { onSuccess: () => onChangeInternal(false) },
          );
        } else {
          addMutation.mutate(
            {
              id: item.id,
              sk: item.sk,
              userId,
              workspaceId,
            },
            { onSuccess: () => onChangeInternal(true) },
          );
        }
      }}
    >
      {ready ? (
        <IconStarSolid width="12" fill="#aaa" />
      ) : (
        <Spinner width="12" />
      )}
    </Button>
  );
};

export default FavoriteButton;
